/**
 * Дата рождения: ввод `ДД.ММ.ГГГГ` ↔ хранение ISO `ГГГГ-ММ-ДД`.
 *
 * ОДИН МОДУЛЬ НА ОБА ФРОНТА. Раньше их было два — `sso-web/.../onboarding/`
 * (с маской ввода) и `id-web/.../personal/` (без неё), — и они успели
 * разойтись: в мастере точки расставлялись сами, а в «Личных данных» человек
 * должен был набрать их вручную, чего цифровая клавиатура iOS
 * (`inputMode="numeric"`) физически не позволяет. Поле было невозможно
 * заполнить с телефона (аудит 2026-08-20, В4).
 *
 * Нативный `<input type="date">` не используется намеренно: порядок его полей
 * задаёт локаль ОС, а не наша, и подпись «ДД.ММ.ГГГГ» начинает врать половине
 * людей.
 *
 * ВРЕМЯ — UTC. Возраст на сервере считается по UTC-дате (модуль `profile`), и
 * клиент обязан считать так же: иначе в Алма-Ате (UTC+6) человек в день
 * шестнадцатилетия видел бы «16» на шесть часов раньше, чем это признаёт
 * сервер, — и получал бы отказ на форме, которая только что показала «всё в
 * порядке». Зависимостей у модуля нет: `packages/shared` собирается и для
 * сервера, где `@internationalized/date` не установлен.
 */

export type BirthDateError = "required" | "format" | "unreal" | "future";

export type BirthDateParseResult = { ok: true; iso: string } | { ok: false; error: BirthDateError };

/**
 * Самый ранний год, который принимаем за дату рождения.
 *
 * Экспортируется, потому что серверный разбор (`modules/profile/_internal/age.ts`)
 * обязан отсекать ровно то же: у него на входе не только человек за формой, но и
 * чужие выгрузки, где `1753-01-01` (минимум MSSQL `datetime`) и `1900-01-01` —
 * типовые заглушки «поле не заполнено». Разойдись эти границы, сервер записал бы
 * возрастную группу по мусору, а форма витрины объявила бы собственные данные
 * сервера ошибкой на первом же `onBlur`.
 */
export const MIN_BIRTH_YEAR = 1900;

/** Точки расставляются по мере набора: человек вводит только цифры. */
export function maskBirthDateInput(raw: string): string {
	const digits = raw.replace(/\D/g, "").slice(0, 8);
	const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(
		(part) => part.length > 0,
	);
	return parts.join(".");
}

/** `1998-04-23` → `23.04.1998`. Пустое значение остаётся пустым. */
export function formatBirthDate(iso: string | null | undefined): string {
	if (!iso) return "";
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
	if (!match) return "";
	return `${match[3]}.${match[2]}.${match[1]}`;
}

/**
 * `23.04.1998` → `1998-04-23`.
 *
 * Проверка на существование даты обязательна: `new Date(1998, 1, 31)` молча
 * превращается в 3 марта, и человек «родился» не тогда, когда написал. Поэтому
 * дата собирается в UTC и сверяется с введёнными числами покомпонентно.
 *
 * Разделителем принимаем точку, дефис и слэш: люди вставляют дату из паспорта,
 * из письма и из другой формы, и отвергать `23/04/1998` не за что.
 */
export function parseBirthDate(raw: string): BirthDateParseResult {
	const trimmed = raw.trim();
	if (trimmed.length === 0) return { ok: false, error: "required" };

	const match = /^(\d{2})[.\-/](\d{2})[.\-/](\d{4})$/.exec(trimmed);
	if (!match) return { ok: false, error: "format" };

	const day = Number(match[1]);
	const month = Number(match[2]);
	const year = Number(match[3]);
	const date = new Date(Date.UTC(year, month - 1, day));
	const real =
		date.getUTCFullYear() === year &&
		date.getUTCMonth() === month - 1 &&
		date.getUTCDate() === day &&
		year >= MIN_BIRTH_YEAR;
	if (!real) return { ok: false, error: "unreal" };
	if (date.getTime() > Date.now()) return { ok: false, error: "future" };

	return { ok: true, iso: `${match[3]}-${match[2]}-${match[1]}` };
}

/**
 * Полных лет на сегодня по UTC — тем же счётом, что на сервере.
 *
 * Возвращает `null` для строки, которая не разбирается в дату: «возраст 0»
 * здесь означал бы «младенец», а не «непонятно что введено».
 */
export function yearsSinceBirth(iso: string, now: Date = new Date()): number | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
	if (!match) return null;
	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);

	let years = now.getUTCFullYear() - year;
	const monthNow = now.getUTCMonth() + 1;
	const dayNow = now.getUTCDate();
	if (monthNow < month || (monthNow === month && dayNow < day)) years -= 1;
	return years;
}

/** Дата в будущем — заведомо не дата рождения. Сравнение по UTC. */
export function isFutureBirthDate(iso: string, now: Date = new Date()): boolean {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
	if (!match) return false;
	return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) > now.getTime();
}
